import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "flemo";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getReviewSchedulePreview,
  REVIEW_QUALITIES,
  type ReviewQuality,
  type ReviewSchedule,
  type ReviewSchedulePreview,
  type ReviewWord,
} from "@/api/smartReview";
import { useReviewWordsQuery, useSubmitReviewMutation } from "@/hooks/queries";
import { useSpeech } from "@/hooks/useSpeech";
import { trackEvent } from "@/lib/analytics";
import { logger } from "@/lib/logger";
import { nativeBridge } from "@/lib/native";
import { useAuthStore } from "@/store/authStore";

export type ReviewQuestion = {
  word: string;
  meaning: string;
  wordId: string;
  reviewStage: number;
};

export type ReviewRatingOption = ReviewSchedulePreview;

export type ReviewResult = {
  question: ReviewQuestion;
  quality: ReviewQuality;
  schedule: ReviewSchedule;
};

type Phase = "loading" | "review" | "result";

export function useSmartReview() {
  const { pop } = useNavigate();
  const queryClient = useQueryClient();
  const userId = useAuthStore(state => state.user?.id);
  const { speak, isMuted, toggleMute } = useSpeech();
  const { data: reviewWords, isPending, isError } = useReviewWordsQuery(true);
  const { mutateAsync: submitResult } = useSubmitReviewMutation();

  const [phase, setPhase] = useState<Phase>("loading");
  const [questions, setQuestions] = useState<ReviewQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnswerRevealed, setIsAnswerRevealed] = useState(false);
  const [pendingQuality, setPendingQuality] = useState<ReviewQuality | null>(null);
  const [submissionError, setSubmissionError] = useState(false);
  const [results, setResults] = useState<ReviewResult[]>([]);

  const questionsBuiltRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const currentQuestion = questions[currentIndex] ?? null;
  const ratingOptions = useMemo(
    () =>
      currentQuestion
        ? REVIEW_QUALITIES.map(quality => getReviewSchedulePreview(currentQuestion.reviewStage, quality))
        : [],
    [currentQuestion],
  );

  useEffect(() => {
    if (questionsBuiltRef.current || !reviewWords || reviewWords.length === 0) {
      return;
    }

    const built = reviewWords.map((word: ReviewWord) => ({
      word: word.word,
      meaning: word.meaning,
      wordId: word.word_id,
      reviewStage: word.review_stage,
    }));

    questionsBuiltRef.current = true;
    setQuestions(built);
    setPhase("review");
    trackEvent("smart_review_started", { word_count: built.length });
  }, [reviewWords]);

  useEffect(() => {
    if (phase !== "review" || !currentQuestion || isMuted) {
      return;
    }

    speak(currentQuestion.word);
  }, [phase, currentIndex, currentQuestion, isMuted, speak]);

  const revealAnswer = useCallback(() => {
    if (!currentQuestion || isAnswerRevealed || pendingQuality) {
      return;
    }

    nativeBridge.system.haptic("light");
    setIsAnswerRevealed(true);

    for (const option of ratingOptions) {
      trackEvent("smart_review_rating_shown", {
        word_id: currentQuestion.wordId,
        quality: option.quality,
        interval_days: option.interval_days,
        review_stage: option.review_stage,
      });
    }
  }, [currentQuestion, isAnswerRevealed, pendingQuality, ratingOptions]);

  const selectQuality = useCallback(
    async (quality: ReviewQuality) => {
      if (!currentQuestion || !isAnswerRevealed || pendingQuality || isSubmittingRef.current) {
        return;
      }

      const preview = getReviewSchedulePreview(currentQuestion.reviewStage, quality);
      isSubmittingRef.current = true;
      nativeBridge.system.haptic("light");
      setPendingQuality(quality);
      setSubmissionError(false);

      trackEvent("smart_review_rating_selected", {
        word_id: currentQuestion.wordId,
        quality,
        interval_days: preview.interval_days,
        review_stage: preview.review_stage,
      });

      try {
        const schedule = await submitResult({ wordId: currentQuestion.wordId, quality });

        trackEvent("smart_review_schedule_saved", {
          word_id: currentQuestion.wordId,
          quality: schedule.quality,
          interval_days: schedule.interval_days,
          review_stage: schedule.review_stage,
        });
        trackEvent("smart_review_question_answered", {
          word_id: currentQuestion.wordId,
          is_correct: quality !== "again",
          quality: schedule.quality,
          interval_days: schedule.interval_days,
          review_stage: schedule.review_stage,
        });

        const newResult: ReviewResult = { question: currentQuestion, quality, schedule };
        const allResults = [...results, newResult];
        setResults(allResults);

        if (currentIndex >= questions.length - 1) {
          trackEvent("smart_review_completed", {
            total: allResults.length,
            again_count: allResults.filter(result => result.quality === "again").length,
            hard_count: allResults.filter(result => result.quality === "hard").length,
            normal_count: allResults.filter(result => result.quality === "normal").length,
            easy_count: allResults.filter(result => result.quality === "easy").length,
            graduated_count: allResults.filter(result => result.schedule.is_graduated).length,
          });
          void queryClient.invalidateQueries({ queryKey: ["reviewWords", userId] });
          setPhase("result");
        } else {
          setCurrentIndex(index => index + 1);
          setIsAnswerRevealed(false);
        }
      } catch (error) {
        setSubmissionError(true);
        trackEvent("smart_review_schedule_failed", {
          word_id: currentQuestion.wordId,
          quality,
          interval_days: preview.interval_days,
          review_stage: preview.review_stage,
        });
        logger.error({
          domain: "smart-review",
          message: "복습 일정 저장 실패",
          error,
          data: { wordId: currentQuestion.wordId, quality },
        });
      } finally {
        isSubmittingRef.current = false;
        setPendingQuality(null);
      }
    },
    [
      currentQuestion,
      currentIndex,
      isAnswerRevealed,
      pendingQuality,
      questions.length,
      queryClient,
      results,
      submitResult,
      userId,
    ],
  );

  const exitToHome = useCallback(() => {
    if (phase !== "result") {
      trackEvent("smart_review_exited", {
        phase,
        current_index: currentIndex,
        total_count: questions.length,
      });
    }

    pop();
  }, [currentIndex, phase, pop, questions.length]);

  const speakWord = useCallback((text: string) => speak(text), [speak]);

  const handleToggleMute = useCallback(() => {
    if (isMuted && phase === "review" && currentQuestion) {
      speak(currentQuestion.word);
    }

    toggleMute();
  }, [currentQuestion, isMuted, phase, speak, toggleMute]);

  return useMemo(() => {
    const isEmpty = !isError && !isPending && (reviewWords?.length ?? 0) === 0;

    return {
      phase,
      questions,
      currentIndex,
      currentQuestion,
      ratingOptions,
      isAnswerRevealed,
      pendingQuality,
      submissionError,
      results,
      isLoading: isPending || (phase === "loading" && !isError && !isEmpty),
      isError,
      isEmpty,
      isMuted,
      revealAnswer,
      selectQuality,
      exitToHome,
      speakWord,
      toggleMute: handleToggleMute,
    };
  }, [
    phase,
    questions,
    currentIndex,
    currentQuestion,
    ratingOptions,
    isAnswerRevealed,
    pendingQuality,
    submissionError,
    results,
    isPending,
    isError,
    reviewWords,
    isMuted,
    revealAnswer,
    selectQuality,
    exitToHome,
    speakWord,
    handleToggleMute,
  ]);
}
